// The Phaser side of liquid terrain (docs/28). Owned by the LightingLayer like the weather, which
// rebuilds it with the light map and passes on the mode: Full draws the surface shader and the
// particles, Soft the shader at reduced strength, Off nothing (the tiles' own two-frame ripple
// still runs); reduce motion stands the shader still and stops every particle.
//
// Where it draws (see LightingLayer's draw order):
//   light (bottom)   the surface shader's texture, rendered at art resolution — under the weather's
//                    ground (fog lies over the water) and the light map (night darkens it)
//   low              lava spits, smoke and steam, the odd leaping fish and its rings, leaves and
//                    petals drifting on the water (all below the units)
// Nothing here is drawn on the overlay camera.
import Phaser from 'phaser';
import type { TileData } from '../../core/sim/types';
import type { DayLight } from '../lighting/daylight';
import { cellHash } from '../tiling/hash';
import type { Kit, TiledBoard } from '../tiling/types';
import type { Season } from '../weather/season';
import type { Weather } from '../weather/weather';
import { LIQUID_CODE, liquidCells, liquidFrozen, liquidLook, type Liquid, type LiquidLook } from './liquid';
import { LIQUID_FRAG } from './liquidShader';
import { ViewRender } from '../board/viewRender';

export interface LiquidOptions {
  /** 1 full, 0.55 soft. */
  strength: number;
  /** Particles (Full only). */
  particles: boolean;
  /** Reduce motion: the surface stands still and nothing moves. */
  still: boolean;
}

export interface LiquidInfo { cells: Record<string, number>; frozen: boolean; shader: boolean; particles: string[]; fish: number }

type Board = { w: number; h: number; tiles: ReadonlyArray<TileData | undefined> };

let serial = 0;

function ensureTextures(scene: Phaser.Scene): void {
  const tm = scene.textures;
  if (!tm.exists('fk-lq-ring')) {
    // a flat ring on the water, 7×3
    const c = tm.createCanvas('fk-lq-ring', 7, 3)!; const x = c.getContext(); x.fillStyle = '#fff';
    x.fillRect(2, 0, 3, 1); x.fillRect(2, 2, 3, 1); x.fillRect(0, 1, 2, 1); x.fillRect(5, 1, 2, 1); c.refresh();
  }
  if (!tm.exists('fk-lq-fish')) {
    // a little silver fish, nose right: a body, a darker tail
    const c = tm.createCanvas('fk-lq-fish', 4, 2)!; const x = c.getContext();
    x.fillStyle = '#e8eef5'; x.fillRect(1, 0, 3, 1); x.fillRect(2, 1, 2, 1); x.fillStyle = '#8fa3b8'; x.fillRect(0, 1, 2, 1); c.refresh();
  }
}

export class LiquidLayer {
  private objects: Phaser.GameObjects.GameObject[] = [];
  private emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  private maskKey?: string;
  private shader?: Phaser.GameObjects.Shader;
  /** The surface pass, rendered only where the camera looks (and only round the liquid's squares). */
  private view?: ViewRender;
  private texKey?: string;
  private lastRender = -1;
  private opts: LiquidOptions = { strength: 1, particles: true, still: false };
  private look?: LiquidLook;
  private time = 0;
  private frozenAt = 0;
  private cells: Array<Liquid | undefined> = [];
  private frozen = false;
  private particleKinds: string[] = [];
  /** Open water squares (water on three sides or more) a fish may leap from, and when the next one does. */
  private pools: Array<{ x: number; y: number }> = [];
  private fish?: { body: Phaser.GameObjects.Particles.ParticleEmitter; ring: Phaser.GameObjects.Particles.ParticleEmitter; next: number };
  private fishCount = 0;
  /** docs/93 §5.6: the squares of every pool a water foe is in (no fish leaps there). */
  private quiet = new Set<number>();
  private boardW = 0;

  constructor(private readonly scene: Phaser.Scene, private readonly low: Phaser.GameObjects.Layer, private readonly light: Phaser.GameObjects.Layer, private readonly cell: number) {
    ensureTextures(scene);
  }

  /** `liquids`: each square's liquid as the commissioned art draws it (docs/186 EA5), instead of the kit's Oryx sets. */
  build(board: Board, kit: Kit, tiled: TiledBoard | undefined, day: DayLight, weather: Weather, season: Season | undefined, opts: LiquidOptions, liquids?: ReadonlyArray<Liquid | undefined>): void {
    this.clear();
    this.opts = opts;
    const cells = liquids && liquids.length === board.w * board.h ? [...liquids] : liquidCells(board, kit);
    this.cells = cells;
    if (!cells.some(Boolean)) return;
    this.frozen = liquidFrozen(kit.key, weather, season);
    const look = liquidLook(day, weather, { frozen: this.frozen, season });
    this.look = look;
    const { scene, cell } = this;
    const W = board.w * cell, H = board.h * cell, k = opts.strength;
    this.frozenAt = (cellHash(`${board.w}x${board.h}`, board.w, board.h, 'lq-time') % 600) / 10;

    if (scene.game.renderer.type === Phaser.WEBGL) {
      const key = `fk-lq-mask-${++serial}`;
      const mask = scene.textures.createCanvas(key, board.w, board.h)!;
      const ctx = mask.getContext();
      const img = ctx.createImageData(board.w, board.h);
      for (let y = 0; y < board.h; y++) for (let x = 0; x < board.w; x++) {
        const i = y * board.w + x, liquid = cells[i];
        img.data[i * 4] = liquid ? LIQUID_CODE[liquid] * 50 : 0;
        img.data[i * 4 + 1] = liquid && (tiled?.cells[i]?.props.length ?? 0) > 0 ? 255 : 0;
        img.data[i * 4 + 2] = cellHash('liquid', x, y) % 256;
        img.data[i * 4 + 3] = 255;
      }
      ctx.putImageData(img, 0, 0); mask.refresh(); mask.setFilter(Phaser.Textures.FilterMode.NEAREST);
      this.maskKey = key;
      const s = new Phaser.GameObjects.Shader(scene, {
        name: 'fkLiquidSurface',
        fragmentSource: LIQUID_FRAG,
        setupUniforms: (set: (name: string, value: unknown) => void) => {
          set('uMask', 0);
          set('uSize', [W, H]);
          set('uCells', [board.w, board.h]);
          set('uTime', this.opts.still ? this.frozenAt : this.time / 1000);
          set('uGlint', look.glint * k);
          set('uLap', look.lap * k);
          set('uSwell', look.swell * k);
          set('uChop', look.chop);
          set('uFlow', [...look.flow]);
          set('uRings', this.opts.still ? 0 : look.rings);
          set('uRingSize', look.ringSize);
          set('uDimples', this.opts.still ? 0 : look.dimples);
          set('uDrips', this.opts.still ? 0 : look.drips);
          set('uLava', look.lava * k);
          set('uLavaBubbles', this.opts.still ? 0 : look.lavaBubbles);
          set('uSheen', look.sheen * k);
          set('uMudBubbles', this.opts.still ? 0 : look.mudBubbles);
          set('uIce', look.ice);
          set('uSky', [...look.sky]);
        },
      }, 0, 0, W, H, [key]);
      const tex = `fk-lq-surface-${serial}`;
      s.setRenderToTexture(tex);
      const surface = scene.add.image(0, 0, tex).setOrigin(0, 0).setDisplaySize(W, H);
      // docs/147 PG5: the shader draws nothing outside the liquids' area (the texture is clear there),
      // so the surface is drawn over that area only, not over the whole board
      const at = liquidArea(cells, board.w, cell);
      const x0 = Math.max(0, at.x), y0 = Math.max(0, at.y), x1 = Math.min(W, at.x + at.w), y1 = Math.min(H, at.y + at.h);
      surface.setCrop(x0, y0, Math.max(0, x1 - x0), Math.max(0, y1 - y0));
      this.light.addAt(surface, 0);
      this.shader = s; this.texKey = tex; this.objects.push(surface, s);
      this.view = new ViewRender(s, scene.cameras.main, W, H, at);
      this.lastRender = -1;
      // under reduce motion the surface is drawn once and left
      if (opts.still) s.renderImmediate();
    }

    if (opts.particles && !opts.still) this.buildParticles(board, look);
  }

  private buildParticles(board: Board, look: LiquidLook): void {
    const { scene, cell, cells } = this;
    const at = (x: number, y: number): Liquid | undefined => (x < 0 || y < 0 || x >= board.w || y >= board.h ? undefined : cells[y * board.w + x]);
    const of = (want: readonly Liquid[]) => {
      const out: Array<{ x: number; y: number }> = [];
      for (let y = 0; y < board.h; y++) for (let x = 0; x < board.w; x++) if (want.includes(at(x, y)!)) out.push({ x, y });
      return out;
    };
    const emit = (kind: string, tex: string, cfg: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig): Phaser.GameObjects.Particles.ParticleEmitter => {
      const e = scene.add.particles(0, 0, tex, cfg);
      this.low.add(e); this.emitters.push(e);
      if (!this.particleKinds.includes(kind)) this.particleKinds.push(kind);
      return e;
    };
    // a random point well inside one of `squares` (clear of the shore art)
    const zone = (squares: Array<{ x: number; y: number }>, inset: number): Phaser.Types.GameObjects.Particles.ParticleEmitterRandomZoneConfig => ({
      type: 'random',
      source: { getRandomPoint: (p: Phaser.Types.Math.Vector2Like) => { const s = squares[Math.floor(Math.random() * squares.length)]!; p.x = (s.x + inset + Math.random() * (1 - 2 * inset)) * cell; p.y = (s.y + inset + Math.random() * (1 - 2 * inset)) * cell; } },
    });
    const water = (p: { x: number; y: number }): boolean => { const l = at(Math.floor(p.x / cell), Math.floor(p.y / cell)); return l === 'water' || l === 'deep'; };

    const lava = of(['lava']);
    if (lava.length) {
      // spits: a few bright drops thrown up out of the lava, falling back
      emit('lava-spits', 'fk-lt-dot', {
        emitZone: zone(lava, 0.2), frequency: 1000 / Math.max(0.4, lava.length * 0.1), quantity: { min: 1, max: 3 },
        lifespan: { min: 420, max: 760 }, speedX: { min: -9, max: 9 }, speedY: { min: -30, max: -14 }, gravityY: 70,
        tint: [0xffe08a, 0xffa040, 0xff6a2a], blendMode: Phaser.BlendModes.ADD, alpha: { onEmit: () => 0, onUpdate: (_p, _k, t) => Math.min(1, (1 - t) * 3) },
        advance: 800,
      });
      // a thin dark smoke lifting off the crust now and then
      emit('lava-smoke', 'fk-lt-soft', {
        emitZone: zone(lava, 0.25), frequency: 1000 / Math.max(0.15, lava.length * 0.025), lifespan: { min: 2600, max: 4000 },
        speedX: { min: look.flow[0] * 0.6, max: look.flow[0] * 1.4 }, speedY: { min: -9, max: -5 }, scale: { start: 0.18, end: 0.6 }, tint: 0x2a1c18,
        alpha: { onEmit: () => 0, onUpdate: (_p, _k, t) => 0.14 * Math.sin(t * Math.PI) }, advance: 3000,
      });
      if (look.steam > 0) {
        // rain or snow hissing into it: pale puffs of steam
        emit('steam', 'fk-lt-soft', {
          emitZone: zone(lava, 0.15), frequency: 1000 / Math.max(0.3, lava.length * 0.18 * look.steam), lifespan: { min: 1200, max: 2000 },
          speedX: { min: look.flow[0] * 1.5, max: look.flow[0] * 3 }, speedY: { min: -16, max: -9 }, scale: { start: 0.12, end: 0.55 }, tint: 0xeef2f6,
          alpha: { onEmit: () => 0, onUpdate: (_p, _k, t) => 0.3 * Math.sin(t * Math.PI) }, advance: 1500,
        });
      }
    }

    const wet = of(['water', 'deep']);
    if (wet.length && !this.frozen) {
      // open water: water on at least three sides (a river two squares wide has some)
      const open = wet.filter((s) => [[1, 0], [-1, 0], [0, 1], [0, -1]].filter(([dx, dy]) => { const l = at(s.x + dx!, s.y + dy!); return l === 'water' || l === 'deep'; }).length >= 3);
      if (look.floaters && wet.length >= 3) {
        // leaves (or blossom) afloat, turning slowly as the surface carries them
        const tint = look.floaters === 'leaves' ? [0xd9822b, 0xe8a23a, 0xb5541f, 0xc9b23d] : [0xffc9dc, 0xffe0ea, 0xffffff, 0xffb3cf];
        emit(look.floaters === 'leaves' ? 'floating-leaves' : 'floating-petals', look.floaters === 'leaves' ? 'fk-lt-leaf' : 'fk-lt-petal', {
          emitZone: zone(open.length ? open : wet, 0.25), frequency: 1000 / Math.max(0.08, wet.length * 0.02), lifespan: { min: 7000, max: 11000 },
          speedX: { min: look.flow[0] * 0.5, max: look.flow[0] * 0.9 }, speedY: { min: look.flow[1] * 0.3, max: look.flow[1] * 0.7 },
          rotate: { min: -40, max: 40 }, tint,
          alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => (water(p) ? 0.85 : 0) * Math.min(1, t * 5, (1 - t) * 4) },
          advance: 9000,
        });
      }
      if (look.fish && open.length > 0) {
        // now and then a fish leaps: a ring where it breaks the water, the arc, a ring where it lands
        const ring = emit('fish', 'fk-lq-ring', {
          emitting: false, lifespan: 700, scale: { start: 0.6, end: 1.9 }, alpha: { start: 0.55, end: 0 }, tint: 0xe6f0ff,
        });
        const body = emit('fish', 'fk-lq-fish', {
          emitting: false, lifespan: 620, gravityY: 110, speedY: -34,
          deathCallback: (p: Phaser.GameObjects.Particles.Particle) => ring.emitParticleAt(Math.round(p.x), Math.round(p.y), 1),
          rotate: { onEmit: () => 0, onUpdate: (p) => (Math.atan2(p.velocityY, p.velocityX) * 180) / Math.PI },
        });
        this.pools = open;
        this.fish = { body, ring, next: 0 };
      }
    }
  }

  /**
   * docs/93 §5.6: no fish leaps in a pool a water foe lurks in — every water square joined to a
   * square under one of `foes` (the pools are flooded square by square over the liquid cells).
   */
  quietPools(foes: ReadonlyArray<{ x: number; y: number }>, w: number): void {
    this.boardW = w;
    this.quiet = new Set();
    const h = w > 0 ? Math.floor(this.cells.length / w) : 0;
    const water = (i: number) => this.cells[i] === 'water' || this.cells[i] === 'deep';
    const stack = foes.map((t) => t.y * w + t.x).filter((i) => i >= 0 && i < this.cells.length && water(i));
    while (stack.length) {
      const i = stack.pop()!;
      if (this.quiet.has(i)) continue;
      this.quiet.add(i);
      const x = i % w, y = Math.floor(i / w);
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const nx = x + dx, ny = y + dy, j = ny * w + nx;
        if (nx >= 0 && ny >= 0 && nx < w && ny < h && water(j) && !this.quiet.has(j)) stack.push(j);
      }
    }
  }

  /** A fish leaps from an open water square every 6–13 s. */
  private leap(time: number): void {
    const f = this.fish;
    if (!f) return;
    if (f.next === 0) { f.next = time + 2500 + Math.random() * 5000; return; }
    if (time < f.next) return;
    f.next = time + 6000 + Math.random() * 7000;
    const free = this.quiet.size ? this.pools.filter((p) => !this.quiet.has(p.y * this.boardW + p.x)) : this.pools;
    if (!free.length) return;
    const s = free[Math.floor(Math.random() * free.length)]!;
    const x = (s.x + 0.3 + Math.random() * 0.4) * this.cell, y = (s.y + 0.4 + Math.random() * 0.3) * this.cell;
    const dir = Math.random() < 0.5 ? -1 : 1;
    f.ring.emitParticleAt(Math.round(x), Math.round(y), 1);
    f.body.setParticleSpeed(dir * (8 + Math.random() * 6), -34);
    f.body.emitParticleAt(x, y, 1);
    this.fishCount++;
  }

  update(time: number): void {
    this.time = time;
    if (this.opts.still) return;
    if (this.view && (this.lastRender < 0 || time - this.lastRender >= 50 || this.view.needed)) { this.lastRender = time; this.view.render(); }
    this.leap(time);
  }

  /** Review hook: a fish leaps now (when there is open water and the sky allows it). */
  leapNow(): void { if (this.fish) { this.fish.next = this.time; this.leap(this.time); } }

  info(): LiquidInfo | undefined {
    if (!this.look) return undefined;
    const cells: Record<string, number> = {};
    for (const c of this.cells) if (c) cells[c] = (cells[c] ?? 0) + 1;
    return { cells, frozen: this.frozen, shader: !!this.shader, particles: [...this.particleKinds], fish: this.fishCount };
  }

  clear(): void {
    for (const o of this.objects) o.destroy();
    for (const e of this.emitters) e.destroy();
    this.objects = []; this.emitters = []; this.shader = undefined; this.view = undefined; this.particleKinds = []; this.fish = undefined; this.pools = [];
    if (this.maskKey) this.scene.textures.remove(this.maskKey);
    if (this.texKey && this.scene.textures.exists(this.texKey)) this.scene.textures.remove(this.texKey);
    this.maskKey = undefined; this.texKey = undefined; this.look = undefined; this.cells = []; this.frozen = false; this.fishCount = 0;
  }
}

/**
 * Where the surface pass draws anything (board px): the liquid squares' bounds and two squares
 * round them for the shores the shader reaches onto. Elsewhere the first full render stands.
 */
export function liquidArea(cells: ReadonlyArray<Liquid | undefined | null>, w: number, cell: number): { x: number; y: number; w: number; h: number } {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  cells.forEach((c, i) => { if (!c) return; const x = i % w, y = Math.floor(i / w); x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); });
  if (x0 > x1) return { x: 0, y: 0, w: 0, h: 0 };
  const pad = 2;
  return { x: (x0 - pad) * cell, y: (y0 - pad) * cell, w: (x1 - x0 + 1 + 2 * pad) * cell, h: (y1 - y0 + 1 + 2 * pad) * cell };
}
